import { I18n } from "@assets/strings";
import React, { JSX } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { Carousel } from "@domain/carousel/carousel";
import { DetailedPerson, Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { useIsMobile } from "@utils/hooks";
import { Bloc, Row } from "@ui/components/layout";
import { TextSkeleton } from "@ui/components/skeleton/basicSkeletons";
import { FollowButton } from "./followButton";
import { ImageSize, ProfilePicture, ProfilePictureSkeleton } from "./profilePicture";

interface UserViewProps extends React.HTMLAttributes<HTMLDivElement> {
	user: Person;
	pictureSize?: ImageSize | number;
	renderBottomAccessory?: () => JSX.Element;
	disabled?: boolean;
	gap?: number;
	alignStart?: boolean;
	showDescription?: boolean;
	hidePicture?: boolean;
	followInfos?: DetailedPerson;
	isAuthor?: boolean;
	squareSize?: number;
	inline?: boolean;
	descMaxWidth?: boolean;
	realAuthor?: Person;
	carousel: Carousel;
}
export const UserView: React.FC<UserViewProps> = ({
	user,
	pictureSize = "default",
	squareSize: number,
	gap,
	disabled,
	renderBottomAccessory,
	alignStart,
	showDescription,
	hidePicture,
	children,
	followInfos,
	isAuthor,
	inline,
	descMaxWidth,
	realAuthor,
	carousel,
	...htmlProps
}) => {
	const isMobile = useIsMobile();

	return !inline ? (
		<Container {...htmlProps} disabled={disabled || !user.id}>
			<Link to={Path.ReferenceCarousel({ id: carousel.id, slug: carousel.slug, category: carousel.category })}>
				<Content gap={gap} alignStart={alignStart}>
					<ProfilePicture
						alt={I18n.t("seo.profile.picture", { name: fullName(user) })}
						picture={!hidePicture ? user.avatar : undefined}
						size={pictureSize}
						fallbackType={user.type}
					/>
					<div>
						<div>
							<Name className="primaryName">{fullName(user)}</Name>
							{children}
						</div>
						{showDescription && !realAuthor && (
							<DescriptionWrapper>
								<Description descMaxWidth={descMaxWidth}>{user.description}</Description>
							</DescriptionWrapper>
						)}
						{!!realAuthor && (
							<DescriptionWrapper>
								<Description descMaxWidth={descMaxWidth}>{`${I18n.t("article.thumbnail.from")} ${fullName(
									realAuthor
								)}`}</Description>
							</DescriptionWrapper>
						)}
						{!isAuthor && followInfos && (
							<StyledFollow gap={gap} alignStart={alignStart}>
								{" "}
								<FollowButton isOrg={followInfos.type === "INSTITUTE"} profile={followInfos} />
							</StyledFollow>
						)}
						{renderBottomAccessory?.()}
					</div>
				</Content>
			</Link>
		</Container>
	) : !isMobile ? (
		<Row style={{ margin: 20, marginTop: 10, width: "-webkit-fill-available" }}>
			<Bloc>
				<ProfilePicture
					alt={I18n.t("seo.profile.picture", { name: fullName(user) })}
					picture={!hidePicture ? user.avatar : undefined}
					size={pictureSize}
					fallbackType={user.type}
				/>
			</Bloc>
			<Bloc style={{ marginLeft: 15, marginTop: 20 }}>
				<Row>
					<Name>{fullName(user)}</Name>
				</Row>
				{showDescription && <Row>{user.description}</Row>}
			</Bloc>
			{children}
		</Row>
	) : (
		<>
			<Row style={{ margin: 20, marginBottom: 0, marginTop: 10 }}>
				<Bloc>
					<ProfilePicture
						alt={I18n.t("seo.profile.picture", { name: fullName(user) })}
						picture={!hidePicture ? user.avatar : undefined}
						size={pictureSize}
						fallbackType={user.type}
					/>
				</Bloc>
				<Bloc style={{ marginLeft: 15, marginTop: 20 }}>
					<Row>
						<Name>{fullName(user)}</Name>
					</Row>
					{showDescription && <Row>{user.description}</Row>}
				</Bloc>
			</Row>
			{children}
		</>
	);
};

export const UserViewSkeleton: React.FC<Omit<UserViewProps, "user">> = ({
	pictureSize,
	gap,
	alignStart,
	...htmlProps
}) => {
	return (
		<Container {...htmlProps}>
			<Content gap={gap} alignStart={alignStart}>
				<ProfilePictureSkeleton size={pictureSize} />
				<TextSkeleton grow height={10} style={{ marginTop: 10 }} />
			</Content>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	pointer-events: ${({ disabled }) => (disabled ? "none" : "all")};
	${({ theme }) => theme.textStyles.text};
`;

const Content = styled.div.withConfig({
	shouldForwardProp: (prop) => !["alignStart"].includes(prop),
})<{ gap?: number; alignStart?: boolean }>`
	display: flex;
	align-items: ${({ alignStart }) => (alignStart ? "flex-start" : "center")};

	> :first-child {
		margin-right: ${({ gap = 7 }) => gap}px;
	}
`;

const StyledFollow = styled.div.withConfig({
	shouldForwardProp: (prop) => !["alignStart"].includes(prop),
})<{ gap?: number; alignStart?: boolean }>`
	display: table;
	table-layout: fixed;
	padding-right: 5px;
	margin-top: 8px;
`;

const Name = styled.span`
	${({ theme }) => theme.textStyles.secondaryFontFamily};
	word-break: break-word;
`;

const DescriptionWrapper = styled.div`
	width: 100%;
	display: table;
	table-layout: fixed;
	padding-right: 5px;
`;

const Description = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "descMaxWidth",
})<{ descMaxWidth?: boolean }>`
	${({ theme }) => theme.textStyles.lighter};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	overflow: hidden;
	text-overflow: ellipsis;
	-webkit-line-clamp: 1;
	-webkit-box-orient: horizontal;
	max-width: ${({ descMaxWidth }) => (descMaxWidth ? "30%" : undefined)};
`;
